import React from 'react';
import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove, removeArrayItem, updateArrayItem } from 'lib/helpers';

interface UseSortableListOptions<Model> {
    value: Model[];
    onChange: (value: Partial<Model>[]) => void;
}

interface UseSortableListResult<Model> {
    items: number[];
    openEdit: boolean;
    openAdd: boolean;
    currentItem: Partial<Model>;
    handleDragEnd: (event: DragEndEvent) => void;
    handleDeleteItem: (index: number) => void;
    handleSubmit: (item: Partial<Model>) => void;
    handleEditItem: (index: number) => void;
    handleAddItem: () => void;
    handleClose: () => void;
}

export function useSortableList<Model extends { index?: number }>(
    options: UseSortableListOptions<Model>
): UseSortableListResult<Model> {
    const { value, onChange } = options;

    const [openEdit, setEdit] = React.useState(false);
    const [openAdd, setAdd] = React.useState(false);
    const [currentIndex, setCurrentIndex] = React.useState(-1);
    const currentItem: Partial<Model> = React.useMemo(() => value[currentIndex], [currentIndex, value]);

    // Generate stable unique IDs for each item so @dnd-kit can track items
    // across reorders and animate correctly instead of snapping back.
    const idCounter = React.useRef(0);
    const idMap = React.useRef(new WeakMap<object, number>());
    const items = React.useMemo(
        () =>
            value.map((item: Model) => {
                const existing = idMap.current.get(item);
                if (existing !== undefined) {
                    return existing;
                }
                const id = idCounter.current++;
                idMap.current.set(item, id);
                return id;
            }),
        [value]
    );

    const handleDragEnd = React.useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event;
            if (over && active.id !== over.id) {
                const oldIndex = items.indexOf(Number(active.id));
                const newIndex = items.indexOf(Number(over.id));
                if (oldIndex !== -1 && newIndex !== -1) {
                    onChange(arrayMove([...value], oldIndex, newIndex));
                }
            }
        },
        [items, onChange, value]
    );

    const handleDeleteItem = React.useCallback(
        (index: number) => {
            onChange(removeArrayItem(value, index));
        },
        [onChange, value]
    );

    const handleSubmit = React.useCallback(
        (item: Partial<Model>) => {
            const arr = updateArrayItem(value, currentIndex, item);
            if (arr[currentIndex]) {
                arr[currentIndex].index = currentIndex + 1;
            }
            onChange(arr);
        },
        [value, currentIndex, onChange]
    );

    const handleEditItem = React.useCallback((index: number) => {
        setCurrentIndex(index);
        setEdit(true);
    }, []);

    const handleAddItem = React.useCallback(() => {
        setCurrentIndex(value.length);
        setAdd(true);
    }, [value.length]);

    const handleClose = React.useCallback(() => {
        setEdit(false);
        setAdd(false);
        setCurrentIndex(-1);
    }, []);

    return {
        items,
        openEdit,
        openAdd,
        currentItem,
        handleDragEnd,
        handleDeleteItem,
        handleSubmit,
        handleEditItem,
        handleAddItem,
        handleClose
    };
}
